/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { throwMethodNotImplementedError } from '@squiz/dxp-console-support';
import { Observable } from 'rxjs';
import { JsonObject } from 'type-fest';
import {
  HTTP_METHOD_DELETE,
  HTTP_METHOD_GET,
  HTTP_METHOD_HEAD,
  HTTP_METHOD_OPTIONS,
  HTTP_METHOD_PATCH,
  HTTP_METHOD_POST,
  HTTP_METHOD_PUT,
  HttpMethod,
} from '../../../Constants/Methods/Methods.constant';
import { HttpRequestOptions } from '../../../Types/RequestOptions/RequestOptions.type';
import { HttpAdapterContract, HttpBaseAdapterNewDto } from './Http.adapter.dto';

/**
 * A http adapter.
 */
export type HttpAdapter = HttpBaseAdapter & HttpAdapterContract;

/**
 * The base http adapter.
 */
export abstract class HttpBaseAdapter {
  /**
   * The optional request options to use as a basis for each request.
   */
  protected readonly baseOptions: Omit<HttpRequestOptions, 'body'>;

  /**
   * Creates an instance of HttpBaseAdapter.
   * @param dto The options to accept.
   */
  public constructor({ baseOptions }: HttpBaseAdapterNewDto) {
    // cspell:disable-next-line
    // Add @babel/plugin-proposal-nullish-coalescing-operator (https://git.io/vb4Se) to the 'plugins' section of your
    // Babel config to enable transformation.
    // this.baseOptions = baseOptions ?? {};
    this.baseOptions = baseOptions === undefined ? {} : baseOptions;
  }

  /**
   * Make a DELETE request.
   * @template T The http request body type.
   * @template R The http response type.
   * @param {string} url The request url.
   */
  public delete<T = JsonObject, R = unknown>(url: string, options: HttpRequestOptions<T> = {}): Observable<R> {
    return this.request<T, R>(HTTP_METHOD_DELETE, url, options);
  }

  /**
   * Make a GET request.
   * @template T The http request body type.
   * @template R The http response type.
   */
  public get<T = JsonObject, R = unknown>(url: string, options: HttpRequestOptions<T> = {}): Observable<R> {
    return this.request<T, R>(HTTP_METHOD_GET, url, options);
  }

  /**
   * Make a HEAD request.
   * @template T The http request body type.
   * @template R The http response type.
   */
  public head<T = JsonObject, R = unknown>(url: string, options: HttpRequestOptions<T> = {}): Observable<R> {
    return this.request<T, R>(HTTP_METHOD_HEAD, url, options);
  }

  /**
   * Make an OPTIONS request.
   * @template T The http request body type.
   * @template R The http response type.
   */
  public options<T = JsonObject, R = unknown>(url: string, options: HttpRequestOptions<T> = {}): Observable<R> {
    return this.request<T, R>(HTTP_METHOD_OPTIONS, url, options);
  }

  /**
   * Make a PATCH request.
   * @template T The http request body type.
   * @template R The http response type.
   */
  public patch<T = JsonObject, R = unknown>(url: string, options: HttpRequestOptions<T> = {}): Observable<R> {
    return this.request<T, R>(HTTP_METHOD_PATCH, url, options);
  }

  /**
   * Make a POST request.
   * @template T The http request body type.
   * @template R The http response type.
   */
  public post<T = JsonObject, R = unknown>(url: string, options: HttpRequestOptions<T> = {}): Observable<R> {
    return this.request<T, R>(HTTP_METHOD_POST, url, options);
  }

  /**
   * Make a PUT request.
   * @template T The http request body type.
   * @template R The http response type.
   */
  public put<T = JsonObject, R = unknown>(url: string, options: HttpRequestOptions<T> = {}): Observable<R> {
    return this.request<T, R>(HTTP_METHOD_PUT, url, options);
  }

  /**
   * Make a request.
   * @template T The http request body type.
   * @template R The http response type.
   */
  public request<T = JsonObject, R = unknown>(
    method: HttpMethod,
    url: string,
    options: HttpRequestOptions<T> = {},
  ): Observable<R> {
    return throwMethodNotImplementedError(`${this.constructor.name}.prototype.request`);
  }
}
